/**
 * @lodex-pro/web-shared/shell/AppShell — reusable nav chrome for all lodeX Pro web apps.
 *
 * Phase 9 / Plan 09-03 — shared shell used by web-dispatch, web-billing, web-portal,
 * web-admin. Implements the Surface Inventory §1 layout from the UI-SPEC.
 *
 * Layout:
 *   - 240px fixed left nav (bg-secondary): the COMPANY's logo (or initials) and name
 *     at the top (TenantBrand), the nav items, and a small "lodeX Pro" product mark
 *     at the bottom (quick 261013-srg PR 0 — the company's identity replaced the
 *     old "lodeX Pro / Hauler Tenant" header, and the product mark moved down).
 *   - 56px fixed top bar (bg-secondary, bottom border) with page title + headerRight slot.
 *   - Scrollable main content region.
 *
 * The 24px TenantBanner under the title was removed in the same PR: it only ever
 * showed the "Hauler Tenant" placeholder, and the real company name now sits at
 * the top of the sidebar.
 *
 * Sign-out button MUST carry aria-label="Sign out" (UI-SPEC accessibility requirement).
 * Icon-only controls without aria-label are a build-blocking defect per UI-SPEC.
 *
 * Typography: 4 sizes (12/14/16/20px), 2 weights (400/600) only. No 700.
 *
 * Spec: UI-SPEC Surface Inventory §1; CLAUDE.md §4 (design tokens, branding);
 *       T-09-03-03 (AppShell auth-gate is UX only; API enforces three-layer security).
 */

import * as React from 'react';
import { LogOut, PanelLeftClose, PanelLeft } from 'lucide-react';
import { NavItem } from './NavItem.js';
import { TenantBrand, type TenantLogo } from './TenantBrand.js';
import { roleDisplayLabel } from '../auth/role-labels.js';

// Quick 260614-s5w (#9) — persist the nav collapsed state so it survives reloads.
const NAV_COLLAPSED_KEY = 'lodex-nav-collapsed';

function loadNavCollapsed(): boolean {
  try {
    return localStorage.getItem(NAV_COLLAPSED_KEY) === 'true';
  } catch {
    return false;
  }
}

/** A single navigation section entry passed to AppShell. */
/**
 * Height of the shell top bar, in px.
 *
 * Exported so anything that has to sit clear of the bar derives from the SAME
 * number instead of keeping a second copy that can drift. web-admin's shell
 * renders its own top bar and uses this too.
 */
export const SHELL_HEADER_HEIGHT_PX = 56;

/**
 * `offset` for sonner's <Toaster>. Toasts default to `top: 24px`, which lands them
 * on top of the top bar and covers the user/avatar cluster for as long as the
 * toast is visible. This drops them clear of the bar with a 16px gap. `right`
 * restates sonner's own default so the value is fully explicit rather than half
 * inherited and half overridden.
 *
 * NOT a rendering bug fix, and worth being precise about because it was first
 * reported as one: at rest a toast is opaque (`rgb(0,0,0)`, `opacity: 1`) and
 * sits above everything (sonner sets `z-index: 999999999` on its own fixed
 * container, and the top bar is `position: static`). It obscured the header, it
 * never garbled it — what looked like interleaved text was a screenshot of the
 * entry animation, where the element measures `top: -12.7` mid-transition. This
 * is a placement preference, not a defect.
 */
export const TOASTER_OFFSET = {
  top: `${SHELL_HEADER_HEIGHT_PX + 16}px`,
  right: '24px',
};

export interface NavSection {
  /** Unique identifier for this nav section (used for active state comparison). */
  readonly id: string;
  /** Human-readable nav label. */
  readonly label: string;
  /** Icon to render next to the label (Lucide icon or any React node). */
  readonly icon: React.ReactNode;
}

export interface AppShellProps {
  /** Navigation sections to render in the left nav. */
  readonly nav: readonly NavSection[];
  /** ID of the currently active nav section. */
  readonly activeNavId: string;
  /** Called when a nav item is clicked. Receives the section id. */
  readonly onNavigate: (id: string) => void;
  /** Page title shown in the top bar (Display — 20px semibold). */
  readonly pageTitle: string;
  /** Active company name for the left nav header; null while it loads. */
  readonly tenantName: string | null;
  /** The company's logo; null/omitted shows initials + name. */
  readonly tenantLogo?: TenantLogo | null;
  /** Display name of the signed-in user. Used for avatar initials + bottom-nav label. */
  readonly userName: string;
  /**
   * The signed-in user's role CODE (e.g. 'DISPATCH_MANAGER'). Rendered as a small
   * label under the name (mapped to a human string via ROLE_DISPLAY_LABELS) so the
   * badge shows WHO is signed in AND their permission level. Omitted when
   * null/undefined/empty.
   */
  readonly role?: string | null;
  /** Called when the sign-out icon button is clicked. */
  readonly onSignOut: () => void;
  /**
   * Optional slot for the right side of the top bar.
   * Plan 09-04 fills this with a notification bell + user menu.
   */
  readonly headerRight?: React.ReactNode;
  /** Main content to render in the scrollable content region. */
  readonly children?: React.ReactNode;
}

/** Returns the first two uppercase initials from a full name. */
function getInitials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

/**
 * Reusable app shell with 240px left nav, 56px top bar, and always-visible tenant banner.
 * All lodeX Pro web apps import this component from @lodex-pro/web-shared (CONTEXT D-06).
 */
export function AppShell({
  nav,
  activeNavId,
  onNavigate,
  pageTitle,
  tenantName,
  tenantLogo,
  userName,
  role,
  onSignOut,
  headerRight,
  children,
}: AppShellProps): React.JSX.Element {
  const initials = getInitials(userName);
  const roleLabel = roleDisplayLabel(role);

  // Quick 260614-s5w (#9) — collapsible left nav for more map/queue space.
  const [navCollapsed, setNavCollapsed] = React.useState<boolean>(loadNavCollapsed);
  const toggleNav = React.useCallback((): void => {
    setNavCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(NAV_COLLAPSED_KEY, String(next));
      } catch {
        /* ignore storage errors */
      }
      return next;
    });
  }, []);

  return (
    <div
      style={{
        display: 'flex',
        height: '100vh',
        overflow: 'hidden',
        backgroundColor: 'var(--color-bg-primary)',
        color: 'var(--color-text-primary)',
        fontFamily: 'var(--font-sans, system-ui, sans-serif)',
      }}
    >
      {/* ── Left nav (240px fixed; collapsible — #9) ── */}
      {!navCollapsed && (
        <nav
          aria-label="Main navigation"
          style={{
            width: '240px',
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: 'var(--color-bg-rail)',
            overflow: 'hidden',
          }}
        >
          {/* Nav header: the company's logo (or initials) and name */}
          <div
            style={{
              padding: '16px',
              flexShrink: 0,
            }}
          >
            <TenantBrand tenantName={tenantName} logo={tenantLogo ?? null} />
          </div>

          {/* Nav items list */}
          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '8px 12px',
              display: 'flex',
              flexDirection: 'column',
              gap: '2px',
            }}
          >
            {nav.map((section) => (
              <NavItem
                key={section.id}
                label={section.label}
                icon={section.icon}
                active={section.id === activeNavId}
                onClick={() => {
                  onNavigate(section.id);
                }}
              />
            ))}
          </div>
          {/* User display moved to the header right (#5 / Quick 260615-ni0). */}

          {/* lodeX Pro product mark — product UI branding (CLAUDE.md branding split) */}
          <div
            data-testid="product-mark"
            style={{
              padding: '12px 16px',
              flexShrink: 0,
              fontSize: '13px',
              fontWeight: 600,
              lineHeight: 1.4,
              color: 'var(--color-text-tertiary)',
            }}
          >
            lodeX Pro
          </div>
        </nav>
      )}

      {/* ── Main column (top bar + tenant banner + content) ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Top bar (56px fixed) */}
        <header
          style={{
            height: `${SHELL_HEADER_HEIGHT_PX}px`,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingLeft: '16px',
            paddingRight: '16px',
            backgroundColor: 'var(--color-bg-rail)',
          }}
        >
          {/* Nav toggle + page title */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {/* Collapse/expand the left nav (#9) */}
            <button
              type="button"
              onClick={toggleNav}
              aria-label={navCollapsed ? 'Show navigation' : 'Hide navigation'}
              aria-pressed={navCollapsed}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '32px',
                height: '32px',
                border: 'none',
                borderRadius: '10px',
                backgroundColor: 'transparent',
                color: 'var(--color-text-secondary)',
                cursor: 'pointer',
                flexShrink: 0,
              }}
            >
              {navCollapsed ? (
                <PanelLeft size={18} aria-hidden="true" />
              ) : (
                <PanelLeftClose size={18} aria-hidden="true" />
              )}
            </button>
            {/* Page title: Display — 20px semibold */}
            <h1
              style={{
                margin: 0,
                fontSize: '20px',
                fontWeight: 600,
                lineHeight: 1.2,
                color: 'var(--color-text-primary)',
              }}
            >
              {pageTitle}
            </h1>
          </div>

          {/* Right cluster: alerts bell (headerRight) + user display (#5 — moved
              here from the sidebar bottom, next to the bell). */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {headerRight !== undefined && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>{headerRight}</div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {/* Avatar: 32×32px circle with initials */}
              <div
                aria-hidden="true"
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  backgroundColor: 'var(--color-bg-tertiary)',
                  color: 'var(--color-accent-blue-dark)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  fontWeight: 600,
                  flexShrink: 0,
                }}
              >
                {initials}
              </div>
              {/* User name (14px) + role label (12px muted) stacked — shows WHO is
                  signed in AND their permission level. Role line omitted when absent. */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  lineHeight: 1.2,
                  overflow: 'hidden',
                  maxWidth: '180px',
                }}
              >
                <span
                  style={{
                    fontSize: '14px',
                    fontWeight: 400,
                    color: 'var(--color-text-primary)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {userName}
                </span>
                {roleLabel !== null && (
                  <span
                    style={{
                      fontSize: '12px',
                      fontWeight: 400,
                      color: 'var(--color-text-secondary)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {roleLabel}
                  </span>
                )}
              </div>
              {/* Sign-out icon button — MUST have aria-label="Sign out" (UI-SPEC a11y) */}
              <button
                type="button"
                onClick={onSignOut}
                aria-label="Sign out"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '28px',
                  height: '28px',
                  padding: '4px',
                  border: 'none',
                  borderRadius: '10px',
                  backgroundColor: 'transparent',
                  color: 'var(--color-text-secondary)',
                  cursor: 'pointer',
                  flexShrink: 0,
                }}
              >
                <LogOut size={16} aria-hidden="true" />
              </button>
            </div>
          </div>
        </header>

        {/* Scrollable content region */}
        <main
          style={{
            flex: 1,
            overflow: 'auto',
            backgroundColor: 'var(--color-bg-primary)',
          }}
        >
          {children}
        </main>
      </div>
    </div>
  );
}
